import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import TooltipPortal from '../components/common/TooltipPortal'; // путь может отличаться

const TutorialContext = createContext();

const LOCAL_STORAGE_KEY = 'dismissedTutorials';
const MOBILE_BREAKPOINT = 768; // Example breakpoint

// Helper function to get dismissed tutorials from localStorage
const getDismissedTutorials = () => {
  try {
    const dismissed = localStorage.getItem(LOCAL_STORAGE_KEY);
    const parsedDismissed = dismissed ? JSON.parse(dismissed) : [];
    return Array.isArray(parsedDismissed) ? [...new Set(parsedDismissed)] : [];
  } catch (error) {
    console.error("Failed to parse dismissed tutorials from localStorage", error);
    return [];
  }
};

export const TutorialProvider = ({ children }) => {
  const location = useLocation();
  const [registeredTips, setRegisteredTips] = useState([]);
  const [dismissedTips, setDismissedTips] = useState(getDismissedTutorials);
  const [activeMobileTip, setActiveMobileTip] = useState(null);
  const [mobileTipsSuppressed, setMobileTipsSuppressed] = useState(false);
  const [blockingOverlays, setBlockingOverlays] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);

  // Effect to handle window resizing for mobile/desktop view changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setMobileTipsSuppressed(false);
  }, [location.pathname]);

  // Effect to update the active mobile tip when tips change
  useEffect(() => {
    if (isMobile && !mobileTipsSuppressed) {
      const firstUndismissedTip = registeredTips.find(tip => !dismissedTips.includes(tip.id));
      setActiveMobileTip(firstUndismissedTip ? firstUndismissedTip.id : null);
    } else {
      // On desktop, all are "active" so we don't need a single active one
      setActiveMobileTip(null);
    }
  }, [registeredTips, dismissedTips, isMobile, mobileTipsSuppressed]);

  const registerTip = useCallback((tip) => {
    setRegisteredTips(prevTips => {
      // Avoid duplicates
      if (prevTips.some(t => t.id === tip.id)) {
        return prevTips;
      }
      return [...prevTips, tip];
    });
  }, []);

  const unregisterTip = useCallback((tipId) => {
    setRegisteredTips(prevTips => prevTips.filter(tip => tip.id !== tipId));
  }, []);

  // Blocking overlays are counted so nested ones each release their own hold.
  const blockTips = useCallback(() => {
    setBlockingOverlays(prevCount => prevCount + 1);
  }, []);

  const unblockTips = useCallback(() => {
    setBlockingOverlays(prevCount => Math.max(0, prevCount - 1));
  }, []);

  const dismissTip = useCallback((tipId) => {
    if (isMobile) {
      setMobileTipsSuppressed(true);
      setActiveMobileTip(null);
    }

    setDismissedTips(prevDismissed => {
      const uniqueDismissed = [...new Set(prevDismissed)];
      if (uniqueDismissed.includes(tipId)) {
        if (uniqueDismissed.length !== prevDismissed.length) {
          try {
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(uniqueDismissed));
          } catch (error) {
            console.error("Failed to save dismissed tutorials to localStorage", error);
          }
        }
        return uniqueDismissed.length === prevDismissed.length
          ? prevDismissed
          : uniqueDismissed;
      }

      const newDismissed = [...uniqueDismissed, tipId];
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newDismissed));
      } catch (error) {
        console.error("Failed to save dismissed tutorials to localStorage", error);
      }
      return newDismissed;
    });
  }, [isMobile]);

  // Вычисляем активные подсказки для отображения
  const activeTips = useMemo(() => {
    if (blockingOverlays > 0) {
      return [];
    }
    if (isMobile) {
      // На мобиле показываем только одну, если она не закрыта
      if (activeMobileTip && !dismissedTips.includes(activeMobileTip)) {
        return registeredTips.filter(tip => tip.id === activeMobileTip);
      }
      return [];
    } else {
      return registeredTips
        .filter(tip => !dismissedTips.includes(tip.id))
        .slice(0, 2);
    }
  }, [registeredTips, dismissedTips, isMobile, activeMobileTip, blockingOverlays]);

  const value = {
    isMobile,
    dismissedTips,
    activeMobileTip,
    registerTip,
    unregisterTip,
    dismissTip,
    blockTips,
    unblockTips,
    tipsBlocked: blockingOverlays > 0,
    activeTips, // добавляем в контекст
  };

  return (
      <TutorialContext.Provider value={value}>
        {children}
        <TooltipPortal />
      </TutorialContext.Provider>  );
};

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (context === undefined) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
};

// Hides tutorial tooltips while a blocking overlay (such as the guest signup
// gate) is on screen. Does nothing when rendered outside a TutorialProvider.
export const useSuppressTutorialTips = (suppressed) => {
  const context = useContext(TutorialContext);
  const blockTips = context?.blockTips;
  const unblockTips = context?.unblockTips;

  useEffect(() => {
    if (!suppressed || !blockTips || !unblockTips) {
      return undefined;
    }

    blockTips();
    return () => unblockTips();
  }, [suppressed, blockTips, unblockTips]);
};
